import { useState, useEffect } from "react";
import {
  CompanySize,
  FundingType,
  CompanyType,
  Company,
} from "@/types/company";
import { HealthcareDomain, Location } from "@/types/core";
import axiosInstance from "@/hooks/axios";
import { isAxiosError } from "axios";

export interface CompanyFilterOptions {
  selectedCompanies: Pick<Company, "id" | "company_name">[];
  selectedCompanySizes: Pick<CompanySize, "id" | "size_range">[];
  selectedFundingTypes: Pick<FundingType, "id" | "funding_type_name">[];
  selectedCompanyTypes: Pick<CompanyType, "id" | "company_type_name">[];
  selectedHealthcareDomains: Pick<HealthcareDomain, "id" | "value">[];
  selectedOperatingStatus: Company["operating_status"][];
  selectedYearFoundedRange: [Company["year_founded"], Company["year_founded"]];
  selectedLocations: Pick<
    Location,
    | "id"
    | "name"
    | "location_type"
    | "parent_name"
    | "parent_location_type"
    | "is_active"
  >[];
}

// hook to fetch company filter options so we dont have to hardcode them
export const useCompanyFilterOptions = () => {
  const [companies, setCompanies] = useState<
    CompanyFilterOptions["selectedCompanies"]
  >([]);
  const [companySizes, setCompanySizes] = useState<
    CompanyFilterOptions["selectedCompanySizes"]
  >([]);
  const [fundingTypes, setFundingTypes] = useState<
    CompanyFilterOptions["selectedFundingTypes"]
  >([]);
  const [companyTypes, setCompanyTypes] = useState<
    CompanyFilterOptions["selectedCompanyTypes"]
  >([]);
  const [healthcareDomains, setHealthcareDomains] = useState<
    CompanyFilterOptions["selectedHealthcareDomains"]
  >([]);
  const [locations, setLocations] = useState<
    CompanyFilterOptions["selectedLocations"]
  >([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchCompanyFilterOptions = async () => {
      try {
        const response = await axiosInstance.get(
          "companies/companies/filter_options/",
        );

        setCompanies(response.data.companies || []);
        setCompanySizes(response.data.company_sizes || []);
        setFundingTypes(response.data.funding_types || []);
        setCompanyTypes(response.data.company_types || []);
        setHealthcareDomains(response.data.healthcare_domains || []);
        setLocations(response.data.locations || []);
      } catch (err) {
        if (isAxiosError(err)) {
          setError(err.message);
        } else if (err instanceof Error) {
          setError(err.message);
        } else {
          setError("An unknown error occurred.");
        }
      } finally {
        setLoading(false);
      }
    };

    fetchCompanyFilterOptions();
  }, []);

  return {
    companies,
    companySizes,
    fundingTypes,
    companyTypes,
    healthcareDomains,
    locations,
    loading,
    error,
  };
};
